import type TabsBroadcastChannel from '@/shared/legacy/lib/tabsBroadcast/tabsBroadcast';
import type { MessageEvent } from '@/shared/legacy/types/poller-message';
import type { Message, Response } from '@/shared/staged/api/poller';
import { BroadcastMessageType } from '@/shared/legacy/lib/tabsBroadcast/tabsBroadcast';
import { createWebsocketClient, reconnectStrategy as defaultReconnectStrategy } from '@/shared/lib/websocket';
import { NotifyJWTHelper } from '@/shared/legacy/lib/util/notify-jwt-helper';
import { httpClient } from '@/shared/lib/http-client';
import { emitter } from '@/shared/lib/emitter';

// Добавляем сюда исключения когда событие описано в нужном месте
type ExcludedEvent = MessageEvent.setting | MessageEvent.notify | MessageEvent.license_request_send | MessageEvent.job;
type EventMap = {
  [k in MessageEvent as Exclude<k, ExcludedEvent>]: Message;
};
declare module '@/shared/lib/emitter/global' {
  // eslint-disable-next-line ts/consistent-type-definitions
  interface EmitterEvents extends EventMap {
  }
}

const reconnectStrategy = defaultReconnectStrategy();
const INVALID_TOKEN_CLOSE_CODE = 4003;

async function fetchToken() {
  const response = await httpClient.defaultClient.get<{ jwt: string }>('/notify_jwt_token').json();
  return response.jwt;
}

export function createWebsocketPoller(url: string, broadcastChannel: TabsBroadcastChannel) {
  let token = NotifyJWTHelper.getToken();
  let seq = -1;
  let closeClient = () => {};

  function handleMessageResponse(response?: Response) {
    if (!response) {
      return;
    }
    response.messages.forEach((message) => {
      const parsed = message.message;
      if (Array.isArray(parsed)) {
        parsed.forEach((m) => {
          emitter.emit(m.event, m);
        });
      } else {
        emitter.emit(parsed.event, parsed);
      }
    });
    seq = response.seq;
  }

  broadcastChannel.onEvent((data) => {
    handleMessageResponse(data);
  });

  broadcastChannel.onBecomeMaster(() => {
    closeClient = create();
  });

  function create() {
    const websocketClient = createWebsocketClient(() => `${url}/websocket?seq=${seq}&token=${token}`, {
      reconnectStrategy: async ({ event, reconnectCount }) => {
        if (event instanceof CloseEvent && event.code === INVALID_TOKEN_CLOSE_CODE) {
          token = await fetchToken();
        }
        // refreshToken может не придти новый токен когда пользователь разлогинился
        if (!token) {
          return false;
        }
        return reconnectStrategy({
          event,
          reconnectCount
        });
      },
      pingInterval: 50000
    }).open();

    websocketClient.on('message', ({ data: message }) => {
      try {
        const data = JSON.parse(message.data);
        handleMessageResponse(data);
        broadcastChannel.postMessage({
          type: BroadcastMessageType.EVENT,
          data
        });
      } catch (err) {
        console.log('wserr', err);
      }
    });

    return () => {
      websocketClient.close();
    };
  }

  const result = {
    close: () => {
      closeClient();
      return result;
    },
    recreate: () => {
      closeClient();
      closeClient = create();
      return result;
    }
  };
  return result;
}
